package com.evoapps.evoshift.domain.model

import com.evoapps.evoshift.domain.market.payroll.PayrollFact
import com.evoapps.evoshift.domain.market.payroll.PayrollProfile
import com.evoapps.evoshift.domain.market.payroll.ProfilePeriod
import com.evoapps.evoshift.domain.market.payroll.profileOn
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus
import kotlinx.serialization.Serializable

// --- PUBLICZNY PROFIL (users/{uid}) ---
@Serializable
data class User(
    val uid: String,
    val displayName: String,
    val photoUrl: String? = null,
    val engagements: Map<String, Engagement> = emptyMap(),
    val isShadow: Boolean,
    val createdAt: String,
    val isSetupComplete: Boolean = false,
    /**
     * SH-138: the language the app shows this person (picked, else the device's: "en", "pl",
     * "uk"); e-mails, pushes and roster PDFs use it. Null until an app has saved it.
     */
    val language: String? = null,
    /** SH-137: the language picked in the app; null: each device follows its own language. */
    val languagePreference: String? = null,
    /** SH-196: the app's colours picked by the person; null: never picked (each device keeps its own). */
    val appearance: Appearance? = null
)

@Serializable
data class Engagement(
    val workplaceId: String,
    val cachedWorkplaceName: String?,
    val role: String?,
    val skills: List<String>,
    val permission: UserPermission,
    val isActive: Boolean,
    val startDate: String,
    val endDate: String? = null,
    /**
     * For someone working in several workplaces: the one the app and the home-screen widget open
     * by default (defaultWorkplaceId).
     */
    val isPrimary: Boolean,
    /** Works on their own or needs supervision (SH-47). */
    val competence: Competence = Competence.INDEPENDENT,
    /**
     * SH-268: this engagement is (also) a group manager's: the group's grant, written only by
     * functions/groupManagers.js. While it is in force the engagement is active with the higher of
     * the group's level and the person's own; it is changed in the group's settings, not in the
     * workplace's Team. Null: an ordinary engagement.
     */
    val groupGrant: GroupGrant? = null,
    /** SH-268: ended when a group's grant went (only the grant had made it); null otherwise. */
    val formerGroupId: String? = null,
    /**
     * SH-270: the feature access functions/permissions.js keeps on the engagement (role,
     * modifications, effective levels); null until it ran. Read only: the app never writes it.
     */
    val access: EngagementAccess? = null,
    /**
     * SH-270: the permission's name as stored when it is not [permission]'s own name
     * (MANAGER_OPERATIONAL for older operational managers, whom [permission] reads as MANAGER; an
     * unknown name read as EMPLOYEE): the key of [EngagementAccess.grants]. Null otherwise.
     */
    val storedPermission: String? = null
) {
    /** SH-268: under a group's grant: shown as the group's manager, edited in the group's settings. */
    val isViaGroup: Boolean get() = groupGrant != null
}

/**
 * SH-268: a group manager's grant on an engagement (users/{uid}.engagements[wp].viaGroupId and
 * the fields beside it). [direct] is the person's own engagement there as it was when the grant
 * came (null: they had none, the grant made the engagement); it comes back when the grant goes.
 */
@Serializable
data class GroupGrant(
    val groupId: String,
    /** Blank for the default group (shown as "All workplaces"). */
    val groupName: String,
    /** The level the group gives (MANAGER or MANAGER_FINANCIAL). */
    val permission: UserPermission,
    val direct: DirectEngagement? = null
)

/** SH-268: a person's own engagement kept under a group's grant (see [GroupGrant.direct]). */
@Serializable
data class DirectEngagement(
    val permission: UserPermission,
    val isActive: Boolean,
    val endDate: String? = null
)

// --- DANE WRAŻLIWE (Rozdzielone!) ---

// Ten obiekt zbiera dane z dwóch źródeł: pliku 'general' i plików 'workplaceId'
@Serializable
data class SensitiveUserData(
    val userId: String,
    // Dane z pliku 'general':
    val email: String?,
    val phoneNumber: String?,
    val address: String?,
    val bankAccountNumber: String?,

    // Dane z plików '{workplaceId}' (zmapowane):
    val financialEngagements: Map<String, FinancialEngagement>
)

@Serializable
data class FinancialEngagement(
    val contractTypeId: String, // SystemContractType name; older documents: "B2B", "UoP", "Zlecenie"
    val declaredNumberOfHours: Int? = null,
    val compensation: CompensationModel, // Sealed Class
    /** Facts the market's payroll needs (age relief, student status, tax statements). */
    val payrollProfile: PayrollProfile = PayrollProfile(),
    /**
     * SH-57: the day ("YYYY-MM-DD") [compensation] applies from; null: since the start (documents
     * written before rate history, and pay never changed since).
     */
    val compensationEffectiveFrom: String? = null,
    /** SH-57: the earlier pay terms, each from its own day until the next one starts. */
    val rateHistory: List<RatePeriod> = emptyList(),
    /**
     * SH-117: the person's number in the accountant's payroll software, which is also their RCP card
     * number there (digits only): the RCP export files match people by it. Null when not set.
     */
    val payrollId: String? = null,
    /**
     * SH-149: the day ("YYYY-MM-DD") [payrollProfile] applies from; null: since the start (documents
     * written before the facts had dates, and facts never changed since).
     */
    val payrollProfileEffectiveFrom: String? = null,
    /** SH-149: the earlier payroll facts, each from its own day until the next one starts. */
    val profileHistory: List<ProfilePeriod> = emptyList(),
    /**
     * SH-212: the day ("YYYY-MM-DD") [contractTypeId] and [declaredNumberOfHours] apply from;
     * null: since the start (documents written before contracts had dates).
     */
    val contractEffectiveFrom: String? = null,
    /**
     * SH-212: the other contracts, each from its own day until the next one starts. Like
     * [rateHistory] and [profileHistory] since SH-212, it may hold periods after the current one
     * (a change planned ahead): the current fields are the ones valid today (see [normalizedOn]).
     */
    val contractHistory: List<ContractPeriod> = emptyList()
) {
    /** The contract of the current fields (see [contractOn] for a given day). */
    val contract: SystemContractType get() = SystemContractType.fromId(contractTypeId)

    /**
     * SH-212: every contract with the day it applies from, oldest first ("" = from the start).
     * SH-250: one after another with the same terms are one period (the earlier start).
     */
    val contractPeriods: List<ContractPeriod>
        get() = DatedPeriods.mergeIdentical(
            (contractHistory + ContractPeriod(contractEffectiveFrom.orEmpty(), contractTypeId, declaredNumberOfHours)).sortedBy { it.effectiveFrom },
            ContractPeriod::sameTermsAs
        )

    /** SH-212: the contract on [date] ("YYYY-MM-DD"); before the first known start, the oldest. */
    fun contractOn(date: String): ContractPeriod =
        contractPeriods.lastOrNull { it.effectiveFrom <= date.take(10) } ?: contractPeriods.first()

    /**
     * SH-212: these terms with the current fields (pay, payroll facts, contract) set to the period
     * valid on [day] ("YYYY-MM-DD") and every other period in the histories, so that readers of the
     * current fields alone (older app versions, the accountant's export) see today's terms. The
     * periods themselves do not change: [compensationOn], [profileOn] and [contractOn] answer the
     * same for every day. functions/timeline.js does the same every morning. SH-250: periods with
     * the same terms one after another are stored as one ([DatedPeriods]).
     */
    fun normalizedOn(day: String): FinancialEngagement {
        val pays = ratePeriods
        val pay = EffectiveDating.currentIndex(pays.map { it.effectiveFrom }, day)
        val profiles = profilePeriods
        val profile = EffectiveDating.currentIndex(profiles.map { it.effectiveFrom }, day)
        val contracts = contractPeriods
        val contractIndex = EffectiveDating.currentIndex(contracts.map { it.effectiveFrom }, day)
        val current = contracts[contractIndex]
        return copy(
            compensation = pays[pay].compensation,
            compensationEffectiveFrom = pays[pay].effectiveFrom.ifEmpty { null },
            rateHistory = pays.filterIndexed { i, _ -> i != pay }.takeLast(MAX_RATE_HISTORY),
            payrollProfile = profiles[profile].profile,
            payrollProfileEffectiveFrom = profiles[profile].effectiveFrom.ifEmpty { null },
            profileHistory = profiles.filterIndexed { i, _ -> i != profile }.takeLast(MAX_RATE_HISTORY),
            contractTypeId = current.contractTypeId,
            declaredNumberOfHours = current.declaredNumberOfHours,
            contractEffectiveFrom = current.effectiveFrom.ifEmpty { null },
            contractHistory = contracts.filterIndexed { i, _ -> i != contractIndex }.takeLast(MAX_RATE_HISTORY)
        )
    }

    /**
     * SH-57: every pay term with the day it applies from, oldest first ("" = from the start).
     * SH-250: one after another with the same terms are one period (the earlier start).
     */
    val ratePeriods: List<RatePeriod>
        get() = DatedPeriods.mergeIdentical(
            (rateHistory + RatePeriod(compensationEffectiveFrom.orEmpty(), compensation)).sortedBy { it.effectiveFrom },
            RatePeriod::sameTermsAs
        )

    /**
     * SH-250: the pay of the current fields ([compensation]) with the day it applies from (null:
     * from the start), and the next change planned after [today] ("YYYY-MM-DD"), when one.
     */
    fun payOutlook(today: String): PayOutlook {
        val periods = ratePeriods
        val from = compensationEffectiveFrom.orEmpty()
        // The merged period holding the current fields' start.
        val index = periods.indexOfLast { it.effectiveFrom <= from }.coerceAtLeast(0)
        val current = periods[index]
        val next = periods.drop(index + 1).firstOrNull { it.effectiveFrom > today.take(10) }
        return PayOutlook(compensation, current.effectiveFrom.ifEmpty { null }, next)
    }

    /**
     * SH-250: these terms with every history stored merged ([DatedPeriods]): the current fields
     * keep the period they hold (from its merged, earlier start), and what applies on any day stays.
     */
    fun withMergedPeriods(): FinancialEngagement {
        fun <P> split(periods: List<P>, currentFrom: String?, from: (P) -> String): Pair<P, List<P>> {
            val index = periods.indexOfLast { from(it) <= currentFrom.orEmpty() }.coerceAtLeast(0)
            return periods[index] to periods.filterIndexed { i, _ -> i != index }
        }
        val (pay, pays) = split(ratePeriods, compensationEffectiveFrom) { it.effectiveFrom }
        val (profile, profiles) = split(profilePeriods, payrollProfileEffectiveFrom) { it.effectiveFrom }
        val (contract, contracts) = split(contractPeriods, contractEffectiveFrom) { it.effectiveFrom }
        return copy(
            compensation = pay.compensation,
            compensationEffectiveFrom = pay.effectiveFrom.ifEmpty { null },
            rateHistory = pays.takeLast(MAX_RATE_HISTORY),
            payrollProfileEffectiveFrom = profile.effectiveFrom.ifEmpty { null },
            profileHistory = profiles.takeLast(MAX_RATE_HISTORY),
            contractEffectiveFrom = contract.effectiveFrom.ifEmpty { null },
            contractHistory = contracts.takeLast(MAX_RATE_HISTORY)
        )
    }

    /** SH-57: the pay terms on [date] ("YYYY-MM-DD"); before the first known start, the oldest. */
    fun compensationOn(date: String): CompensationModel =
        ratePeriods.lastOrNull { it.effectiveFrom <= date.take(10) }?.compensation ?: ratePeriods.first().compensation

    /**
     * SH-57: these terms with [newPay] from [from] ("YYYY-MM-DD"): the old terms go to the history
     * and keep applying before that day. The same pay (or no date) changes nothing in the history;
     * terms that would only have started on or after [from] are replaced.
     */
    fun withPay(newPay: CompensationModel, from: String?): FinancialEngagement {
        if (from.isNullOrBlank() || newPay.sameTermsAs(compensation)) {
            return copy(compensation = newPay)
        }
        val history = ratePeriods.filter { it.effectiveFrom < from }.takeLast(MAX_RATE_HISTORY)
        return copy(compensation = newPay, compensationEffectiveFrom = from, rateHistory = history)
    }

    /**
     * SH-149: every set of payroll facts with the day it applies from, oldest first ("" = from the
     * start). SH-250: one after another with the same facts are one period (the earlier start).
     */
    val profilePeriods: List<ProfilePeriod>
        get() = DatedPeriods.mergeIdentical(
            (profileHistory + ProfilePeriod(payrollProfileEffectiveFrom.orEmpty(), payrollProfile)).sortedBy { it.effectiveFrom }
        ) { a, b -> a.profile == b.profile }

    /**
     * SH-149: the payroll facts on [date] ("YYYY-MM-DD"), with the known ends (under 26, student)
     * applied; before the first known start, the oldest.
     */
    fun profileOn(date: String): PayrollProfile = profilePeriods.profileOn(date, payrollProfile)

    /**
     * SH-149: these terms with [newProfile] from [from] ("YYYY-MM-DD"), as [withPay] does for pay:
     * the old facts go to the history and keep applying before that day. The same facts (or no
     * date) change nothing in the history: only the known ends are updated in place.
     */
    fun withProfile(newProfile: PayrollProfile, from: String?): FinancialEngagement {
        if (from.isNullOrBlank() || newProfile.sameFactsAs(payrollProfile)) {
            return copy(payrollProfile = newProfile)
        }
        val history = profilePeriods.filter { it.effectiveFrom < from }.takeLast(MAX_RATE_HISTORY)
        return copy(payrollProfile = newProfile, payrollProfileEffectiveFrom = from, profileHistory = history)
    }

    /**
     * SH-149: the dated changes of the pay and the payroll facts, oldest first, for the history:
     * each day something changed from, with the new pay (when it changed) and the facts that were
     * switched on or off, including statuses that end on a known day (from the day after it).
     */
    val termsChanges: List<TermsChange>
        get() {
            val pays = ratePeriods
            val profiles = profilePeriods
            val days = mutableSetOf<String>()
            pays.drop(1).forEach { days += it.effectiveFrom }
            profiles.drop(1).forEach { days += it.effectiveFrom }
            // A known end within its period: the status is off from the next day.
            val ends = profiles.mapIndexedNotNull { i, period ->
                val next = profiles.getOrNull(i + 1)?.effectiveFrom
                listOf(PayrollFact.UNDER_26, PayrollFact.STUDENT).mapNotNull { fact ->
                    val end = period.profile.knownEnd(fact) ?: return@mapNotNull null
                    val off = runCatching { LocalDate.parse(end).plus(DatePeriod(days = 1)).toString() }.getOrNull() ?: return@mapNotNull null
                    off.takeIf { it >= period.effectiveFrom && (next == null || it < next) }?.let { it to fact }
                }
            }.flatten()
            ends.forEach { days += it.first }
            return days.filter { it.isNotEmpty() }.sorted().map { day ->
                val payStart = pays.indexOfFirst { it.effectiveFrom == day }
                val newPay = pays.getOrNull(payStart)?.takeIf { payStart > 0 }?.compensation
                val before = profilePeriods.profileOn(dayBefore(day), payrollProfile).facts
                val after = profileOn(day).facts
                val changed = PayrollFact.entries.filter { before[it] != after[it] }.map { fact ->
                    FactChange(fact, after.getValue(fact), knownEnd = ends.any { it.first == day && it.second == fact } && after[fact] == false)
                }
                TermsChange(day, newPay, changed)
            }.filter { it.pay != null || it.facts.isNotEmpty() }
        }

    private fun dayBefore(day: String): String =
        runCatching { LocalDate.parse(day).plus(DatePeriod(days = -1)).toString() }.getOrElse { "" }

    companion object {
        /** Enough for the 12-month averages of sick pay with a change every month. */
        const val MAX_RATE_HISTORY = 24
    }
}

/** SH-149: what changed on [effectiveFrom]: the new [pay] (null: unchanged) and the payroll facts switched. */
data class TermsChange(val effectiveFrom: String, val pay: CompensationModel?, val facts: List<FactChange>)

/** SH-149: [fact] switched on or off; [knownEnd]: off because the status reached its known end. */
data class FactChange(val fact: PayrollFact, val on: Boolean, val knownEnd: Boolean = false)

/**
 * SH-212: a contract (its type and, for hourly pay, the monthly hours it is for) that applies from
 * [effectiveFrom] ("YYYY-MM-DD"; "" = from the start) until the next one starts. The working-time
 * share (FTE) of a salary stays in the pay terms (CompensationModel.MonthlyFixed.fte), which are
 * dated already: it is not copied here.
 */
@Serializable
data class ContractPeriod(
    val effectiveFrom: String,
    val contractTypeId: String,
    val declaredNumberOfHours: Int? = null
) {
    val contract: SystemContractType get() = SystemContractType.fromId(contractTypeId)

    /** SH-250: the same contract type and declared hours. */
    fun sameTermsAs(other: ContractPeriod): Boolean =
        contractTypeId == other.contractTypeId && declaredNumberOfHours == other.declaredNumberOfHours
}

/**
 * SH-250: the current pay ([pay]), the day it applies from ([since], "YYYY-MM-DD"; null: from the
 * start) and the next change already planned ([next]), when one.
 */
data class PayOutlook(val pay: CompensationModel, val since: String?, val next: RatePeriod?)

/**
 * SH-250: dated periods that follow one another (each lasts until the next one starts) with the
 * same terms are one period: they say nothing the first one does not. Whatever applies on any day
 * stays the same, so the cost snapshots of past rosters keep matching.
 */
object DatedPeriods {
    /**
     * [periods] (sorted by start) with each one that has the same terms as the one before it
     * folded into that one, which keeps its earlier start. Periods with any different term stay.
     */
    fun <T> mergeIdentical(periods: List<T>, sameTerms: (T, T) -> Boolean): List<T> {
        val merged = ArrayList<T>(periods.size)
        periods.forEach { period ->
            val last = merged.lastOrNull()
            if (last == null || !sameTerms(last, period)) merged += period
        }
        return merged
    }
}

/** SH-212: the one rule of effective dating every dated history follows. */
object EffectiveDating {
    /**
     * The index of the period in force on [day] among periods starting on [starts] (sorted,
     * "YYYY-MM-DD", "" = from the start): the last one started by then; before the first start,
     * the first. functions/timeline.js (currentIndex) agrees, both tested against
     * firebase/tests/golden/timeline/cases.json.
     */
    fun currentIndex(starts: List<String>, day: String): Int {
        val d = day.take(10)
        val started = starts.indexOfLast { it <= d }
        return if (started >= 0) started else 0
    }
}

/** SH-57: pay terms that apply from [effectiveFrom] ("YYYY-MM-DD"; "" = from the start). */
@Serializable
data class RatePeriod(val effectiveFrom: String, val compensation: CompensationModel) {
    /**
     * SH-250: whether both pay the same: the model, the rate or salary, the working-time share and
     * the currency. The stored employer cost is left out: no calculation uses it (the market's
     * payroll rules compute the cost), so it never keeps periods apart; a merge keeps the earlier
     * period's value.
     */
    fun sameTermsAs(other: RatePeriod): Boolean = compensation.withoutStoredEmployerCost() == other.compensation.withoutStoredEmployerCost()
}

/** SH-250: these terms without the stored employer cost (unused: the payroll rules compute the cost). */
fun CompensationModel.withoutStoredEmployerCost(): CompensationModel = when (this) {
    is CompensationModel.Hourly -> copy(employerCost = 0.0)
    is CompensationModel.MonthlyFixed -> copy(employerCost = 0.0)
}

/** Whether two pay terms pay the same (the stored employer cost is not computed from them any more). */
fun CompensationModel.sameTermsAs(other: CompensationModel): Boolean = when (this) {
    is CompensationModel.Hourly -> other is CompensationModel.Hourly && rate == other.rate
    is CompensationModel.MonthlyFixed -> other is CompensationModel.MonthlyFixed && baseSalary == other.baseSalary && fte == other.fte
}

// Sealed Class - idealne do logiki biznesowej
@Serializable
sealed class CompensationModel {
    abstract val employerCost: Double

    @Serializable
    data class Hourly(
        val rate: Double,
        override val employerCost: Double,
        val currency: String = "PLN"
    ) : CompensationModel()

    @Serializable
    data class MonthlyFixed(
        val baseSalary: Double,
        override val employerCost: Double,
        val fte: Double, // Wymiar etatu, np. 1.0, 0.5
        val currency: String = "PLN"
    ) : CompensationModel()
}